import { Components, Theme } from '@mui/material'
import '@mui/x-data-grid-premium/themeAugmentation'
import { omneaDataGridClasses } from '../../../dataGridClasses'

const CORNER_RADIUS = 8
const TABLE_BORDER = '1px solid #E7E7ED'
const ROW_BORDER = '1px solid #E0E0E0'
const HEADER_BACKGROUND = '#F6F7FA'

export const MuiDataGrid: Components<Omit<Theme, 'components'>>['MuiDataGrid'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      border: 'none',
      '--DataGrid-overlayHeight': '300px',
      // MUI X v7's built-in `--unstable_DataGrid-radius` drives default top
      // radii on `.MuiDataGrid-main > *:first-child` (i.e. the columnHeaders)
      // and the first cell of the first row in `root--noToolbar` mode. We
      // disable that default here and opt in via modifier classes below; the
      // original `DataGridBase` sx relied on the equivalent by explicitly
      // setting `borderTopLeftRadius: 0` on columnHeaders/virtualScroller.
      '--unstable_DataGrid-radius': '0px',

      // Bottom corners of the whole grid (last direct child — footer, or the
      // scroller when the footer is hidden).
      '& > *:last-child': {
        borderBottomLeftRadius: CORNER_RADIUS,
        borderBottomRightRadius: CORNER_RADIUS,
      },

      '& .MuiDataGrid-toolbarContainer': {
        border: 'none !important',
        padding: 0,
        margin: 0,
      },
      '& .MuiDataGrid-main': {
        padding: 0,
        margin: 0,
        borderRadius: 0,
        width: '100%',
        flexGrow: 1,
        maxHeight: '100%',
        height: '100%',
      },
      '& .MuiDataGrid-columnHeaders': {
        border: TABLE_BORDER,
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: 'none',
        background: HEADER_BACKGROUND,
        zIndex: 0,
      },
      '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:first-of-type': {
        paddingLeft: '24px !important',
      },
      '& .MuiDataGrid-columnHeadersInner': {
        overflow: 'visible',
      },
      '& .MuiDataGrid-columnHeader': {
        overflow: 'visible',
        color: theme.palette.neutralLight[900],
        fontSize: 12,
      },
      '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainerContent': {
        justifyContent: 'center',
      },
      '& .MuiDataGrid-columnHeaderTitleContainerContent': {
        width: '100%',
      },
      '& .MuiDataGrid-columnHeader--pinned': {
        zIndex: 2,
        backgroundColor: theme.palette.background.paper,
      },
      '& .MuiDataGrid-pinnedColumns': {
        zIndex: 1,
      },
      '& .MuiDataGrid-pinnedColumnHeaders': {
        zIndex: 1,
        background: HEADER_BACKGROUND,
        boxShadow: 'none',
      },
      '& .MuiDataGrid-footerContainer': {
        border: TABLE_BORDER,
        borderTop: 'none',
        background: HEADER_BACKGROUND,
        borderRadius: 0,
        borderBottomLeftRadius: CORNER_RADIUS,
        borderBottomRightRadius: CORNER_RADIUS,
        maxHeight: 36,
        boxShadow: '0px -2px 4px 0px #00000012',
      },
      '& .MuiDataGrid-footerContainer .MuiTablePagination-root': {
        overflowY: 'hidden',
      },
      '& .MuiDataGrid-row': {
        backgroundColor: theme.palette.neutralLight[0],
        cursor: 'pointer',
        border: `${ROW_BORDER} !important`,
        borderTop: 'none !important',
        borderLeft: 'none !important',
        borderRight: 'none !important',
      },
      '& .MuiDataGrid-filler': {
        border: 'none',
        '&>div': {
          border: 'none',
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellEmpty': {
        paddingRight: '0 !important',
      },
      '& .MuiDataGrid-columnSeparator': {
        opacity: 0,
      },
      '& .MuiDataGrid-cell': {
        border: 'none',
        alignItems: 'center',
        display: 'flex',
      },
      '& .MuiDataGrid-cell:first-of-type': {
        paddingLeft: 22,
      },
      '& .MuiDataGrid-cell:last-of-type': {
        paddingRight: 24,
      },
      '& .MuiDataGrid-cell:focus': {
        outline: 'none !important',
        borderBottom: 'none',
      },
      '& .MuiDataGrid-cell:focus-within': {
        borderBottom: `1px solid ${theme.palette.neutralLight[300]}`,
      },
      '& .MuiDataGrid-cell--editing': {
        backgroundColor: 'rgb(255,215,115, 0.19)',
        color: '#1a3e72',
        '& .MuiInputBase-root': {
          height: '100%',
        },
        '&:first-of-type': {
          paddingLeft: 0,
        },
      },
      '& .Mui-error': {
        backgroundColor: `rgb(126,10,15, ${theme.palette.mode === 'dark' ? 0 : 0.1})`,
        color: theme.palette.error.main,
      },
      '& .MuiDataGrid-cellContent': {
        color: theme.palette.text.secondary,
      },
      '& .MuiDataGrid-headerCell': {
        border: 'none',
      },
      '& .MuiDataGrid-overlay': {
        background: 'transparent',
      },
      '& .MuiDataGrid-virtualScrollerContent': {
        borderRadius: 0,
        height: '100%',
      },
      '& .MuiDataGrid-virtualScroller': {
        border: TABLE_BORDER,
        borderTop: 'none',
        borderBottom: 'none',
        background: theme.palette.neutralLight[0],
      },
      '& .MuiDataGrid-overlayWrapperInner': {
        borderBottom: TABLE_BORDER,
      },
      '&:has(.MuiCircularProgress-root) .MuiDataGrid-virtualScroller': {
        overflow: 'hidden',
      },
      '& .grouping-row': {
        backgroundColor: HEADER_BACKGROUND,
        '&.Mui-selected': {
          backgroundColor: HEADER_BACKGROUND,
        },
      },
      '& .archived-row': {
        backgroundColor: theme.palette.neutralLight[100],
        '& .MuiDataGrid-cell': {
          color: theme.palette.neutralLight[900],
        },
      },
      '& .has-issue-row': {
        borderLeft: `4px solid ${theme.palette.warning.main} !important`,
      },
      '& .MuiDataGrid-cell--pinnedLeft': {
        borderBottom: ROW_BORDER,
        backgroundColor: theme.palette.common.white,
      },
      '& .MuiDataGrid-cell--pinnedRight': {
        borderBottom: ROW_BORDER,
        backgroundColor: theme.palette.common.white,
      },
      '& > *:first-child': {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      },

      // Layout-conditional opt-ins. The wrapping component (e.g. DataGridBase)
      // adds these classes to the root based on its surrounding layout. Each
      // rule is gated on a class being *present* so non-opting grids inherit
      // nothing.
      [`&.${omneaDataGridClasses.roundTop}`]: {
        '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-virtualScroller': {
          borderTopLeftRadius: CORNER_RADIUS,
          borderTopRightRadius: CORNER_RADIUS,
        },
      },
      [`&.${omneaDataGridClasses.roundFirstChildTop}`]: {
        '& > *:first-child': {
          borderTopLeftRadius: CORNER_RADIUS,
          borderTopRightRadius: CORNER_RADIUS,
        },
      },
      [`&.${omneaDataGridClasses.roundPinnedLeftHeaderTop}`]: {
        '& .MuiDataGrid-columnHeader--pinnedLeft': {
          borderTopLeftRadius: CORNER_RADIUS,
        },
      },
      [`&.${omneaDataGridClasses.roundHeaderRowTop}`]: {
        '& .MuiDataGrid-columnHeaders .MuiDataGrid-row--borderBottom': {
          borderTopLeftRadius: CORNER_RADIUS,
          borderTopRightRadius: CORNER_RADIUS,
        },
      },
      [`&.${omneaDataGridClasses.roundBottom}`]: {
        // Last row (may or may not be wrapped in a link)
        '& :not(a) > .MuiDataGrid-row:last-child, & a:last-child > .MuiDataGrid-row': {
          borderBottomLeftRadius: CORNER_RADIUS,
          borderBottomRightRadius: CORNER_RADIUS,
          '& .MuiDataGrid-cell:first-of-type': {
            borderBottomLeftRadius: CORNER_RADIUS,
          },
          '& .MuiDataGrid-cell:last-of-type': {
            borderBottomRightRadius: CORNER_RADIUS,
          },
        },
        '& .MuiDataGrid-virtualScroller': {
          borderBottomLeftRadius: CORNER_RADIUS,
          borderBottomRightRadius: CORNER_RADIUS,
        },
        '& .MuiDataGrid-overlayWrapperInner': {
          borderBottomLeftRadius: CORNER_RADIUS,
          borderBottomRightRadius: CORNER_RADIUS,
        },
      },
      [`&.${omneaDataGridClasses.allowPageScroll}`]: {
        '& .MuiDataGrid-virtualScroller': {
          overflowY: 'visible !important',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
      },
    }),
  },
}
